import { Loader2, Palette, Trash2 } from 'lucide-react';
import { useState } from 'react';
import useSWR from 'swr';

import { AppShell, Breadcrumbs } from '@/components/landing/AppShell';
import { api } from '@/lib/api';
import type { DesignSystemSummary } from '@/lib/schema.types';

/**
 * Design Repository — the library of design systems extracted from reference
 * decks (PDF/PPTX). A system holds the palette / typography / assets used to
 * theme generated lesson packs; pick one on the New Lesson Pack page.
 *
 * New systems are created by the backend extraction script, not from the UI:
 *   npx tsx tools/extract-design-system.ts --name "…" --pdf deck.pdf [--pptx deck.pptx]
 */
export default function DesignRepositoryPage() {
  const { data, error, isLoading, mutate } = useSWR<DesignSystemSummary[]>(
    '/design-systems',
    () => api.designSystems.list(),
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const remove = async (ds: DesignSystemSummary) => {
    if (!window.confirm(`Delete design system "${ds.name}"? Packs already using it keep their look.`)) return;
    setDeletingId(ds._id);
    try {
      await api.designSystems.remove(ds._id);
      await mutate();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AppShell activeNav="design-repository" showSearch={false}>
      <div className="mx-auto w-full max-w-[1080px] px-8 py-8">
        <Breadcrumbs items={[{ label: 'Design Repository' }]} />
        <div className="mt-4 flex items-end justify-between">
          <div>
            <h1 className="text-[26px] font-semibold tracking-tight text-fg">
              Design Repository
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Design systems extracted from reference decks — palettes, type and
              assets used to theme generated lesson packs.
            </p>
          </div>
        </div>

        {isLoading && (
          <div className="mt-12 flex justify-center text-fg-muted">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        {error != null && !isLoading && (
          <div className="mt-8 rounded-lg border border-border-subtle bg-bg-panel p-5 text-sm text-fg-muted">
            Could not load design systems. Is the backend running?
          </div>
        )}

        {!isLoading && !error && (data ?? []).length === 0 && (
          <div className="mt-10 flex flex-col items-center rounded-xl border border-dashed border-border-subtle bg-bg-panel/50 px-8 py-14 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-accent-soft text-accent">
              <Palette size={22} />
            </span>
            <h2 className="mt-4 text-[15px] font-semibold text-fg">
              No design systems yet
            </h2>
            <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-fg-muted">
              Extract one from a reference deck with the backend script:
            </p>
            <code className="mt-3 rounded-md bg-bg-subtle px-3 py-2 font-mono text-[12px] text-fg">
              npx tsx tools/extract-design-system.ts --name "My Theme" --pdf deck.pdf
            </code>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data ?? []).map((ds) => {
            const swatches = [
              ds.palette.primary,
              ds.palette.secondary,
              ds.palette.accent,
              ds.palette.background,
              ds.palette.surface,
              ...(ds.palette.extras ?? []),
            ].filter(Boolean);
            return (
              <div
                key={ds._id}
                className="group flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-bg-panel shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Preview: first rendered page of the source deck, else palette band. */}
                {ds.previewImage ? (
                  <img
                    src={ds.previewImage}
                    alt=""
                    className="h-[150px] w-full object-cover"
                    style={{ background: ds.palette.background }}
                  />
                ) : (
                  <div
                    className="h-[150px] w-full"
                    style={{
                      background: `linear-gradient(135deg, ${ds.palette.secondary} 0%, ${ds.palette.accent} 60%, ${ds.palette.background} 100%)`,
                    }}
                  />
                )}

                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[14px] font-semibold text-fg">{ds.name}</h3>
                    <button
                      onClick={() => remove(ds)}
                      disabled={deletingId === ds._id}
                      className="rounded p-1 text-fg-subtle opacity-0 transition-opacity hover:bg-bg-subtle hover:text-red-500 group-hover:opacity-100"
                      title="Delete design system"
                    >
                      {deletingId === ds._id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                  {ds.description && (
                    <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-fg-muted">
                      {ds.description}
                    </p>
                  )}

                  {/* Palette swatch strip */}
                  <div className="mt-3 flex items-center gap-1.5">
                    {swatches.slice(0, 7).map((c, i) => (
                      <span
                        key={`${c}-${i}`}
                        className="h-5 w-5 rounded-full border border-black/10"
                        style={{ background: c }}
                        title={c}
                      />
                    ))}
                  </div>

                  <div className="mt-auto flex items-center justify-between pt-3 text-[11px] text-fg-subtle">
                    <span className="truncate">
                      {ds.typography.headingFamily || 'Default type'}
                      {ds.typography.bodyFamily &&
                      ds.typography.bodyFamily !== ds.typography.headingFamily
                        ? ` · ${ds.typography.bodyFamily}`
                        : ''}
                    </span>
                    <span className="shrink-0">
                      {ds.assetCount} asset{ds.assetCount === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
